// notifyChadOnBuildApproval — fires when a deal lands in Chad's build-approval queue.
//
// Sends a Twilio SMS to Chad (lookup by email in team_roster) with a deep link
// to the deal so he can act from his phone, AND inserts an in-app notification
// for redundancy on /notifications.
//
// Fire-and-forget: any failure is logged but never blocks the caller. Called
// from TransitionButton (install_meeting → build_submitted), AdminControlsPanel
// (admin override), RoofMasterTable (inline status change), and PipelineView
// (per-card stage dropdown + mass stage update — added Jun 11, 2026 after an
// audit found 17 of 18 recent submissions came through the pipeline dropdown
// with no text fired). Manual SQL writes will not fire the SMS — acceptable.
// Bug class: ANY new UI path that writes stage='build_submitted' must call
// this helper in the same diff. Grep for `notifyChadOnBuildApproval` when
// adding stage-write sites.
//
// Why client-side instead of a Postgres trigger: pg_net isn't installed on this
// project, the existing automation_queue worker only handles homeowner-facing
// twilio_text rows, and a client hook is reversible in a 10-line diff.

//
// Oct 21, 2026: the first payment plan picked at submission rides along
// ("First payment $8,423.17 not in · plan: check by build day"), and Chad is
// never texted about a build he submitted himself (he submits most of them
// from the deal page and approves in the same sitting).

import { supabase } from "./supabase";
import type { Deal } from "./types";
import { planPhrase, usd, type PaymentPlanKey } from "./buildPaymentPlan";

const PORTAL_BASE_URL = "https://app.bardinroofing.com";
const CHAD_EMAIL = "chad@bardinroofing.com";

// property_state intentionally NOT required — the SMS address line only uses
// street + city, and lean callers (PipelineView's PipelineDeal Pick) don't
// fetch state.
type DealForNotify = Pick<Deal, "id" | "homeowner_name" | "property_street" | "property_city">;

/**
 * Format a US 10-digit phone as +1XXXXXXXXXX for Twilio. Returns null if the
 * input doesn't look like a US number.
 */
function toE164(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

function buildAddressLine(deal: DealForNotify): string {
  const parts = [deal.property_street, deal.property_city].filter(Boolean) as string[];
  return parts.join(", ");
}

export interface BuildApprovalExtras {
  /** Who moved the deal. Chad isn't texted about his own submissions. */
  actorId?: string | null;
  /** The first payment plan picked at submission (null when it's in). */
  plan?: { plan: PaymentPlanKey; note: string | null; owed: number | null; label: string } | null;
}

/** "First payment $8,423.17 not in · plan: check by build day" */
export function buildApprovalPlanLine(p: NonNullable<BuildApprovalExtras["plan"]>): string {
  const label = p.label.charAt(0).toUpperCase() + p.label.slice(1);
  const money = p.owed != null && p.owed > 0 ? `${label} ${usd(p.owed)} not in` : `${label} not in`;
  return `${money} · plan: ${planPhrase(p.plan, p.note)}`;
}

/**
 * Fire-and-forget. Caller should NOT await unless they need confirmation.
 * Errors are swallowed and logged via console.warn — never bubble up to the UI.
 */
export async function notifyChadOnBuildApproval(deal: DealForNotify, extras: BuildApprovalExtras = {}): Promise<void> {
  try {
    const { data: chad, error: lookupErr } = await supabase
      .from("team_roster")
      .select("id,full_name,phone")
      .eq("email", CHAD_EMAIL)
      .eq("is_active", true)
      .maybeSingle();

    if (lookupErr) {
      console.warn("[notifyChadOnBuildApproval] team_roster lookup failed:", lookupErr.message);
      return;
    }
    if (!chad) {
      console.warn("[notifyChadOnBuildApproval] Chad row not found / inactive — skipping text.");
      return;
    }
    if (extras.actorId && extras.actorId === chad.id) return; // he submitted it himself

    const homeowner = deal.homeowner_name || "(no name)";
    const addr = buildAddressLine(deal);
    const dealUrl = `${PORTAL_BASE_URL}/deal/${deal.id}`;
    const planLine = extras.plan ? buildApprovalPlanLine(extras.plan) : null;
    const smsBody = `Build approval needed: ${homeowner}${addr ? ` (${addr})` : ""}.${planLine ? ` ${planLine}.` : ""} Tap to review: ${dealUrl}`;

    // 1) In-app notification (always — works even if Chad's phone is missing).
    const inAppPromise = supabase.from("notifications").insert({
      user_id: chad.id,
      type: "chad_build_approval",
      title: `Build approval needed — ${homeowner}`,
      body: [addr, planLine].filter(Boolean).join(" · ") || dealUrl,
      deal_id: deal.id,
      metadata: { url: dealUrl, ...(extras.plan ? { payment_plan: extras.plan.plan, payment_plan_note: extras.plan.note } : {}) },
    });

    // 2) Twilio SMS — only if Chad has a phone on file.
    const phoneE164 = toE164(chad.phone);
    let smsPromise: Promise<unknown> = Promise.resolve();
    if (phoneE164) {
      smsPromise = supabase.functions.invoke("twilio-send-sms", {
        body: {
          to: phoneE164,
          body: smsBody,
          // No deal_id passed: prevents twilio-send-sms v16 from auto-mirroring
          // a text_received notification onto the deal owner (RM). This is a
          // staff-internal alert, not a homeowner conversation log.
          tag: "chad_build_approval",
        },
      });
    } else {
      console.warn("[notifyChadOnBuildApproval] Chad has no phone on team_roster — skipping SMS.");
    }

    const [inAppRes, smsRes] = await Promise.allSettled([inAppPromise, smsPromise]);
    if (inAppRes.status === "rejected") {
      console.warn("[notifyChadOnBuildApproval] in-app insert failed:", inAppRes.reason);
    }
    if (smsRes.status === "rejected") {
      console.warn("[notifyChadOnBuildApproval] SMS send failed:", smsRes.reason);
    }
  } catch (err) {
    console.warn("[notifyChadOnBuildApproval] unexpected error:", err);
  }
}
